import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Loader2, Mail } from 'lucide-react';
import { PageHeader, Badge, Button, Modal, Field, Input, Select, Textarea, EmptyState, Tabs, Segmented } from '../components/ui';
import { STATUS_VAR } from '../lib/viz';
import { FormError } from '../components/forms/common';
import { Can } from '../components/Can';
import { useAuth } from '../stores/auth';
import { outboxApi, whenShort, whenLong, OUTBOX_TONE, type OutboxItem, type OutboxStatus } from '../lib/comms';
import { PhoneInput } from '../components/ui/PhoneInput';
import { formatPhone } from '../lib/phone';

const STATUSES: Array<{ key: OutboxStatus | ''; label: string }> = [
  { key: '', label: 'Everything' }, { key: 'QUEUED', label: 'Queued' }, { key: 'DRAFT', label: 'Draft' },
  { key: 'FAILED', label: 'Failed' }, { key: 'SENT', label: 'Sent' }, { key: 'CANCELLED', label: 'Cancelled' },
];
const COLOUR: Record<OutboxStatus, string> = { SENT: STATUS_VAR.good, QUEUED: STATUS_VAR.warning, DRAFT: 'var(--color-ink-300)', FAILED: STATUS_VAR.critical, CANCELLED: STATUS_VAR.neutral };

/**
 * Every letter and email the platform has written — welcome emails, arrears reminders,
 * deposit letters — and whether it went (prototype §32). Nothing is emailed directly: it
 * waits here until someone sends it, or a worker does once the mail service is connected.
 */
export default function OutboxPage() {
  const qc = useQueryClient();
  const [status, setStatus] = useState<OutboxStatus | ''>('');
  const [kind, setKind] = useState('');
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [open, setOpen] = useState<OutboxItem | null>(null);
  useEffect(() => { const t = setTimeout(() => setQ(search), 300); return () => clearTimeout(t); }, [search]);
  // ?item=<id> opens one letter (My Day's "letter not sent" links here).
  const [params, setParams] = useSearchParams();
  const itemId = params.get('item');
  useEffect(() => {
    if (!itemId) return;
    void outboxApi.get(itemId).then(setOpen).catch(() => undefined).finally(() => { params.delete('item'); setParams(params, { replace: true }); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId]);

  const list = useQuery({
    queryKey: ['/outbox', { status, kind, q }],
    queryFn: () => outboxApi.list({ status: status || undefined, kind: kind || undefined, q: q || undefined, perPage: 100 }),
  });
  const counts = list.data?.counts;
  const waiting = counts ? counts.QUEUED + counts.DRAFT + counts.FAILED : 0;
  return (
    <div className="space-y-2.5">
      <PageHeader title="Outbox" />

      {list.data && (
        <div className={`flex items-start gap-2.5 rounded-lg border px-4 py-2.5 text-[12.5px] ${list.data.transport === 'log' ? 'border-amber-200 bg-amber-50 text-amber-900' : 'border-ink-200 bg-white text-ink-600'}`}>
          <Mail className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            {list.data.transport === 'log'
              ? <>Mail is in <b>development mode</b>: "Send" marks a letter sent and writes it to the server log — nothing reaches a real inbox until the mail service (Amazon SES) is connected.</>
              : <>Sending through <b>{list.data.transport}</b>.</>}
            {waiting > 0 && <> {waiting} waiting to go out.</>}
          </span>
        </div>
      )}

      <Tabs items={STATUSES.map((s) => ({ key: s.key, label: s.label, count: counts && s.key && counts[s.key] > 0 ? counts[s.key] : null }))} value={status} onChange={setStatus} />
      <div className="flex flex-wrap items-center gap-2">
        <div className="hidden flex-1 sm:block" />
        <div className="flex w-full items-center gap-2 sm:w-auto">
          <Select value={kind} onChange={(e) => setKind(e.target.value)} className="min-w-0 flex-1 sm:flex-none" aria-label="Kind">
            <option value="">Every kind</option>
            {list.data?.kinds.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
          </Select>
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…" className="min-w-0 flex-1 sm:w-48 sm:flex-none" />
          {list.isFetching && <Loader2 className="h-4 w-4 animate-spin text-bronze-400" />}
        </div>
      </div>

      {list.isLoading ? (
        <div className="surface space-y-2.5 p-4">{[0, 1, 2].map((i) => <div key={i} className="h-12 animate-pulse rounded-lg bg-ink-100" />)}</div>
      ) : list.error ? (
        <EmptyState icon="TriangleAlert" title="Could not load the outbox" body={(list.error as Error).message} />
      ) : !list.data?.items.length ? (
        <EmptyState icon="Mail" title="Nothing written yet" body="Welcome emails, arrears reminders and deposit letters appear here as the platform writes them." />
      ) : (
        <div className="surface divide-y divide-ink-100 overflow-hidden">
          {list.data.items.map((m) => (
            <button key={m.id} onClick={() => setOpen(m)} className="flex w-full flex-wrap items-start gap-x-4 gap-y-1.5 px-4 py-2.5 text-left transition hover:bg-ink-50/60"
              style={{ boxShadow: `inset 3px 0 0 ${COLOUR[m.status]}` }}>
              <div className="flex w-full items-center gap-2 sm:w-36 sm:flex-col sm:items-start sm:gap-1">
                <Badge value={m.status} tone={OUTBOX_TONE[m.status]} />
                <span className="text-[11.5px] text-ink-400">{m.kindLabel}</span>
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-[13px] font-medium text-ink-900">{m.subject}</div>
                <div className="truncate text-[12px] text-ink-500">
                  {m.toName ? `${m.toName} · ` : ''}{m.channel === 'SMS' ? `Text · ${formatPhone(m.toPhone) || 'no number'}` : m.toEmail ?? 'no address'}{m.propertyName ? ` · ${[m.unitReference, m.propertyName].filter(Boolean).join(', ')}` : ''}
                </div>
                {(m.unfilled.length > 0 || (m.error && m.status !== 'SENT')) && (
                  <div className="mt-0.5 flex items-center gap-1 truncate text-[11.5px] text-red-600"><AlertTriangle className="h-3 w-3 shrink-0" />{m.error ?? `Fill in ${m.unfilled.join(', ')}`}</div>
                )}
              </div>
              <div className="text-[11.5px] tabular-nums text-ink-400 sm:text-right">
                {whenShort(m.createdAt)}
                {m.sentAt && <div className="text-emerald-700">sent {whenShort(m.sentAt)}{m.transport === 'log' ? ' (log)' : ''}</div>}
              </div>
            </button>
          ))}
        </div>
      )}

      {open && <Letter item={open} onClose={() => setOpen(null)} onChanged={(m) => { setOpen(m); void qc.invalidateQueries({ queryKey: ['/outbox'] }); }} />}
    </div>
  );
}

function Letter({ item, onClose, onChanged }: { item: OutboxItem; onClose: () => void; onChanged: (m: OutboxItem) => void }) {
  const { can } = useAuth();
  const unsent = item.status === 'DRAFT' || item.status === 'QUEUED' || item.status === 'FAILED';
  const editable = unsent && can('outbox.update');
  const sms = item.channel === 'SMS';
  const addr = (sms ? item.toPhone : item.toEmail) ?? '';
  const [to, setTo] = useState(addr);
  const [subject, setSubject] = useState(item.subject);
  const [body, setBody] = useState(item.body);
  const [tab, setTab] = useState<'preview' | 'edit'>('preview');
  const dirty = to !== addr || subject !== item.subject || body !== item.body;
  useEffect(() => { setTo(addr); setSubject(item.subject); setBody(item.body); }, [item, addr]);

  const save = async () => (dirty ? outboxApi.update(item.id, { ...(sms ? { toPhone: to.trim() || null } : { toEmail: to.trim() || null }), subject, body }) : item);
  const act = useMutation({
    mutationFn: async (what: 'save' | 'send' | 'retry' | 'cancel') => {
      if (what === 'cancel') return outboxApi.cancel(item.id);
      const saved = await save();
      if (what === 'save') return saved;
      return what === 'retry' ? outboxApi.retry(item.id) : outboxApi.send(item.id);
    },
    onSuccess: onChanged,
  });
  const unfilled = (subject + body).match(/\{\{\s*[\w.]+\s*\}\}/g) ?? [];

  return (
    <Modal open onClose={onClose} title={item.kindLabel} size="lg"
      footer={
        <div className="flex w-full flex-wrap items-center gap-2">
          {unsent && <Can do="outbox.cancel"><Button variant="ghost" onClick={() => act.mutate('cancel')} disabled={act.isPending} className="text-red-600">Cancel letter</Button></Can>}
          <div className="flex-1" />
          {editable && dirty && <Button variant="secondary" onClick={() => act.mutate('save')} disabled={act.isPending}>Save</Button>}
          {(item.status === 'DRAFT' || item.status === 'QUEUED') && <Can do="outbox.send"><Button icon="Send" onClick={() => act.mutate('send')} disabled={act.isPending || unfilled.length > 0}>Send</Button></Can>}
          {item.status === 'FAILED' && <Can do="outbox.send"><Button icon="RotateCcw" onClick={() => act.mutate('retry')} disabled={act.isPending || unfilled.length > 0}>Retry</Button></Can>}
          {!unsent && <Button variant="secondary" onClick={onClose}>Close</Button>}
        </div>
      }>
      <div className="mb-3 flex flex-wrap items-center gap-2 text-[12px] text-ink-500">
        <Badge value={item.status} tone={OUTBOX_TONE[item.status]} />
        <span>Written {whenLong(item.createdAt)}{item.createdByName ? ` by ${item.createdByName}` : ' by the platform'}</span>
        {item.sentAt && <span className="text-emerald-700">· sent {whenLong(item.sentAt)} via {item.transport}</span>}
        {item.attempts > 1 && <span>· {item.attempts} attempts</span>}
        {item.tenancyId && <Link to={`/app/tenancies/${item.tenancyId}`} className="font-medium text-bronze-600 hover:underline">· {item.tenancyReference}</Link>}
      </div>
      {item.error && item.status !== 'SENT' && <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3.5 py-2.5 text-[12.5px] text-red-800">{item.error}</div>}
      {unfilled.length > 0 && <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-[12.5px] text-amber-900">Fill in {[...new Set(unfilled)].join(', ')} before this can be sent.</div>}

      {editable && (
        <Segmented className="mb-3" items={[{ key: 'preview', label: 'Preview' }, { key: 'edit', label: 'Edit' }]} value={tab} onChange={setTab} />
      )}
      {tab === 'edit' && editable ? (
        <div className="space-y-3">
          <Field label={sms ? 'Mobile number' : 'To'}>{sms ? <PhoneInput autoComplete="off" value={to} onChange={setTo} /> : <Input value={to} onChange={(e) => setTo(e.target.value)} className="w-full" type="email" />}</Field>
          <Field label="Subject"><Input value={subject} onChange={(e) => setSubject(e.target.value)} className="w-full" /></Field>
          <Field label={sms ? `Text (${body.length} characters)` : 'Letter'}><Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={sms ? 4 : 14} className="w-full font-mono text-[12.5px]" /></Field>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-ink-200">
          <div className="space-y-0.5 border-b border-ink-100 bg-ink-50 px-4 py-3 text-[12.5px]">
            <div><span className="inline-block w-16 text-ink-400">To</span><span className="text-ink-800">{item.toName ? `${item.toName} <${to || '—'}>` : to || '—'}</span></div>
            <div><span className="inline-block w-16 text-ink-400">Subject</span><span className="font-medium text-ink-900">{subject}</span></div>
          </div>
          <div className="max-h-[50vh] overflow-y-auto whitespace-pre-wrap break-words bg-white px-4 py-4 font-[ui-sans-serif] text-[13px] leading-relaxed text-ink-800">{body}</div>
        </div>
      )}
      {act.error && <div className="mt-3"><FormError error={act.error} /></div>}
    </Modal>
  );
}
